/**
 * gasCalculation.js
 *
 * Gas rise calculation logic for SIBO breath test reports.
 *
 * Time intervals used for all tests:
 *   0, 20, 40, 60, 80, 90, 100, 120, 150, 180 (minutes)
 *
 * Time limits:
 *   - Lactulose / Glucose → 90 minutes (first 6 data points)
 *   - All other tests    → 180 minutes (all 10 data points)
 */

const TIME_INTERVALS = [0, 20, 40, 60, 80, 90, 100, 120, 150, 180];

/**
 * Calculates the gas rise for a single gas channel (H₂, CH₄, or CO₂)
 * using the peak-difference algorithm.
 *
 * Algorithm:
 *  Step 1 — Filter the values array to the allowed time range.
 *  Step 2 — If the first value is the global maximum, ignore it.
 *  Step 3 — For each remaining value, check if there is any smaller
 *            value to its left. If so it is a valid peak; compute
 *            diff = peak - min(left values).
 *  Step 4 — Result = max(all valid diffs).
 *            If no valid diffs exist:
 *              • all values equal → 0
 *              • otherwise       → "Not Conspicuous"
 *
 * @param {Array<number|string>} values
 *   Array of raw gas values aligned to TIME_INTERVALS (use "-", "", or null
 *   to represent missing/invalid readings).
 * @param {number} timeLimit
 *   Upper time bound in minutes — either 90 or 180.
 *
 * @returns {{
 *   numericValue: number|null,
 *   status: string,
 *   isNotConspicuous: boolean,
 *   peakValue: number|null,
 *   peakTime: number|null,
 *   leftMinValue: number|null,
 * }}
 *   numericValue    — The calculated rise (or 0 for all-equal / no data).
 *                     null when result is "Not Conspicuous".
 *   status          — Human-readable string: "<n> ppm" or "Not Conspicuous".
 *   isNotConspicuous — true when no valid increasing pattern was found.
 *   peakValue       — The peak value that produced the maximum diff.
 *   peakTime        — Time (minutes) of that peak.
 *   leftMinValue    — The minimum preceding value used to compute the diff.
 */
function calculateGasRise(values, timeLimit) {
  // ── Step 1: restrict to the allowed time window ──────────────────────────
  const indexLimit = TIME_INTERVALS.indexOf(timeLimit) + 1;

  // Build a clean array of { value, index, time } for valid readings only
  const filteredItems = values
    .slice(0, indexLimit)
    .map((v, i) => ({ raw: v, index: i, time: TIME_INTERVALS[i] }))
    .filter(
      (item) =>
        item.raw !== "-" &&
        item.raw !== "" &&
        item.raw != null &&
        !isNaN(Number(item.raw))
    )
    .map((item) => ({ ...item, value: Number(item.raw) }));

  // Edge case: no usable data at all
  if (filteredItems.length === 0) {
    return {
      numericValue: 0,
      status: "0 ppm",
      isNotConspicuous: false,
      peakValue: null,
      peakTime: null,
      leftMinValue: null,
    };
  }

  // ── Step 2: optionally drop the first value if it is the global max ───────
  const firstValue = filteredItems[0].value;
  const globalMax = Math.max(...filteredItems.map((item) => item.value));

  const workingItems =
    firstValue === globalMax ? filteredItems.slice(1) : filteredItems;

  // ── Step 3: find all valid peaks and their (peak − left-min) differences ─
  const validDiffs = [];

  for (let i = 0; i < workingItems.length; i++) {
    const current = workingItems[i];
    const leftItems = workingItems.slice(0, i);

    // A peak is only valid when at least one preceding value is strictly smaller
    const hasSmaller = leftItems.some((item) => item.value < current.value);
    if (!hasSmaller) continue;

    const leftMin = Math.min(...leftItems.map((item) => item.value));
    const diff = current.value - leftMin;

    validDiffs.push({
      diff,
      peakValue: current.value,
      peakTime: current.time,
      leftMinValue: leftMin,
    });
  }

  // ── Step 4: derive the final result ───────────────────────────────────────
  if (validDiffs.length === 0) {
    // Check whether every filtered value is identical
    const allEqual = filteredItems.every(
      (item) => item.value === filteredItems[0].value
    );

    if (allEqual) {
      // E.g. [20, 20, 20, 20, 20] → 0
      return {
        numericValue: 0,
        status: "0 ppm",
        isNotConspicuous: false,
        peakValue: null,
        peakTime: null,
        leftMinValue: null,
      };
    }

    // No valid increase found → "Not Conspicuous"
    return {
      numericValue: null,
      status: "Not Conspicuous",
      isNotConspicuous: true,
      peakValue: null,
      peakTime: null,
      leftMinValue: null,
    };
  }

  // Take the maximum difference across all valid peaks
  const best = validDiffs.reduce((max, d) => (d.diff > max.diff ? d : max));

  return {
    numericValue: best.diff,
    status: `${best.diff} ppm`,
    isNotConspicuous: false,
    peakValue: best.peakValue,
    peakTime: best.peakTime,
    leftMinValue: best.leftMinValue,
  };
}

/**
 * Wrapper that calculates gas rise for all three gas channels at once.
 *
 * @param {Array<number|string>} h2Values  — H₂ readings aligned to TIME_INTERVALS
 * @param {Array<number|string>} ch4Values — CH₄ readings aligned to TIME_INTERVALS
 * @param {Array<number|string>} co2Values — CO₂ readings aligned to TIME_INTERVALS
 * @param {number} timeLimit               — 90 or 180 minutes
 *
 * @returns {{ h2: object, ch4: object, co2: object }}
 *   Each property has the same shape as the return value of calculateGasRise().
 */
function calculateAllGasResults(h2Values, ch4Values, co2Values, timeLimit) {
  return {
    h2: calculateGasRise(h2Values, timeLimit),
    ch4: calculateGasRise(ch4Values, timeLimit),
    co2: calculateGasRise(co2Values, timeLimit),
  };
}

export { calculateGasRise, calculateAllGasResults, TIME_INTERVALS };
